import React, { useState, useEffect } from "react";
import { getAuthToken } from "./api";
import { auth } from "./firebase";
import toast from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE || "https://mockbot-api1.onrender.com";

const GENERAL_TIPS = [
  { num: "01", h: "Open-ended format", p: "10 adaptive questions across role-fit, technical depth, and behavioral signals." },
  { num: "02", h: "Difficulty calibrates live", p: "Follow-ups get harder when you ace a question, lighter when you stumble." },
  { num: "03", h: "Resume-aware", p: "Drop your resume and we'll pull projects, stacks, and timelines into the script." },
  { num: "04", h: "Speak naturally", p: "Answer aloud — we transcribe and grade clarity, structure, and depth, not keywords." },
  // { num: "05", h: "WhatsApp feedback", p: "A scored rubric, transcript, and replay link arrive on your phone within 5 minutes." },
];

const DIFFICULTY_OPTIONS = [
  { value: "Beginner", note: "Entry level" },
  { value: "Intermediate", note: "2-5 yrs" },
  { value: "Advanced", note: "5+ yrs" },
  { value: "Expert", note: "Staff / Lead" },
];

const EXP_QUICK = ["0", "1", "2", "3", "5", "7", "10"];

const INTERVIEW_CATEGORIES = [
  {
    value: "generic",
    label: "General",
    sub: "Open-ended practice",
    description: "Role-focused mock interviews for any domain.",
    focus: "Adaptive practice",
  },
  {
    value: "service",
    label: "Service Industry",
    sub: "TCS, Infosys, Wipro…",
    description: "Communication, implementation, and practical delivery rounds.",
    focus: "Execution readiness",
  },
  {
    value: "big4",
    label: "Big 4",
    sub: "Deloitte, PwC, EY, KPMG",
    description: "Consulting, analytical thinking, and business communication.",
    focus: "Consulting polish",
  },
];

const SERVICE_COMPANIES = [
  {
    value: "TCS",
    full: "Tata Consultancy Services",
    note: "Mass hiring focused",
    pattern: "Aptitude → Technical → HR",
    focus: ["Aptitude", "Coding", "Project Discussion"],
    recommended: true,
  },
  {
    value: "Infosys",
    full: "Infosys Limited",
    note: "Service-based engineering roles",
    pattern: "Online test → Tech → HR",
    focus: ["DSA Basics", "SQL", "Behavioral"],
  },
  {
    value: "Capgemini",
    full: "Capgemini SE",
    note: "Client project interviews",
    pattern: "Pseudo-code → Tech → HR",
    focus: ["Pseudo-code", "OOP", "Communication"],
  },
  {
    value: "Accenture",
    full: "Accenture plc",
    note: "Consulting-leaning engineering",
    pattern: "Cognitive → Tech → HR",
    focus: ["Cognitive", "Coding", "Comms"],
    recommended: true,
  },
  {
    value: "Cognizant",
    full: "Cognizant Technology Solutions",
    note: "Digital engineering",
    pattern: "Online → Tech → Final",
    focus: ["DSA", "DBMS", "Behavioral"],
  },
  {
    value: "Wipro",
    full: "Wipro Limited",
    note: "Large-scale onboarding",
    pattern: "Aptitude → Tech → Business",
    focus: ["Logical", "Verbal", "Coding"],
  },
];

const BIG4_FIRMS = [
  {
    value: "Deloitte",
    practices: "Consulting · Audit & Assurance · Risk Advisory · Tax",
    focus: ["Case Study", "Business Problem Solving", "Stakeholder Communication", "Analytical Thinking"],
    recommended: true,
  },
  {
    value: "PwC",
    practices: "Advisory · Assurance · Tax · Deals",
    focus: ["Market Sizing", "Case Study", "Client Comms", "Logical Reasoning"],
  },
  {
    value: "EY",
    practices: "Consulting · Strategy · Tax · Assurance",
    focus: ["Guesstimate", "Frameworks", "Stakeholder Mgmt", "Quant Analysis"],
  },
  {
    value: "KPMG",
    practices: "Advisory · Tax · Audit",
    focus: ["Case Math", "Hypothesis Testing", "Exec Presence", "Issue Tree"],
  },
];

function CategoryIcon({ type }) {
  if (type === "service") {
    return (
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19V9" /><path d="M4 9l8-4 8 4v10" /><path d="M9 19v-6h6v6" /><path d="M2 19h20" />
      </svg>
    );
  }
  if (type === "big4") {
    return (
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 9h1" /><path d="M14 9h1" /><path d="M9 13h1" /><path d="M14 13h1" />
      </svg>
    );
  }
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21 7 14.2 2 9.3l6.9-1L12 2z" />
    </svg>
  );
}

function SparkIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
    </svg>
  );
}

// ─── Left panel: General track ─────────────────────────────────────────────
function GeneralPanel() {
  return (
    <section className="setup-panel">
      <div className="setup-panel__head">
        <span className="setup-panel__eyebrow">How it works</span>
        <h2>Five things to know before you start</h2>
        <p>A flexible track that adapts to whatever role you're prepping for. Fill in the session details on the right to begin.</p>
      </div>
      <ol className="setup-guide__list">
        {GENERAL_TIPS.map((step) => (
          <li key={step.num} className="setup-guide__item">
            <span className="setup-guide__num">{step.num}</span>
            <div className="setup-guide__body">
              <div className="setup-guide__h">{step.h}</div>
              <p className="setup-guide__p">{step.p}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="setup-guide__foot">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" />
        </svg>
        <span>Need a specific company? Switch to <strong>Service Industry</strong> or <strong>Big 4</strong> above.</span>
      </div>
    </section>
  );
}

// ─── Left panel: Service Industry ──────────────────────────────────────────
function ServicePanel({ selectedCompany, onSelect }) {
  const [query, setQuery] = useState("");
  const filtered = query.trim()
    ? SERVICE_COMPANIES.filter((c) =>
        (c.value + " " + c.full + " " + c.note).toLowerCase().includes(query.toLowerCase())
      )
    : SERVICE_COMPANIES;

  return (
    <section className="setup-panel">
      <div className="setup-panel__head">
        <span className="setup-panel__eyebrow">Service Industry</span>
        <h2>Pick a company to mirror</h2>
        <p>Question patterns, difficulty pacing, and follow-up style modeled after each firm's typical campus & lateral process.</p>
      </div>

      <div className="co-search-wrap">
        <svg className="co-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          className="form-input"
          style={{ paddingLeft: "2.2rem" }}
          placeholder="Search companies…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="company-card-grid">
        {filtered.map((co) => (
          <button
            key={co.value}
            type="button"
            className={`co-card${selectedCompany === co.value ? " is-on" : ""}`}
            onClick={() => onSelect(co.value)}
          >
            {co.recommended && (
              <span className="co-card__rec"><SparkIcon /> Recommended</span>
            )}
            <div className="co-card__top">
              <span className="co-card__mark">{co.value.slice(0, 1)}</span>
              <div className="co-card__id">
                <div className="co-card__name">{co.value}</div>
                <div className="co-card__full">{co.full}</div>
              </div>
              <span className="co-card__radio" />
            </div>
            <div className="co-card__tag">{co.note}</div>
            <div className="co-card__pattern">{co.pattern}</div>
            <div className="co-card__focus">
              {co.focus.map((f) => <span key={f} className="co-focus-chip">{f}</span>)}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ─── Left panel: Big 4 ─────────────────────────────────────────────────────
function Big4Panel({ selectedFirm, onSelect }) {
  return (
    <section className="setup-panel">
      <div className="setup-panel__head">
        <span className="setup-panel__eyebrow">Big 4 Consulting</span>
        <h2>Consulting case practice</h2>
        <p>Case-led format with structured reasoning, math drills, and stakeholder communication. Adapted per firm's interview philosophy.</p>
      </div>
      <div className="b4-grid">
        {BIG4_FIRMS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`b4-card${selectedFirm === f.value ? " is-on" : ""}`}
            onClick={() => onSelect(f.value)}
          >
            {f.recommended && (
              <span className="b4-card__rec"><SparkIcon /> Recommended</span>
            )}
            <div className="b4-card__top">
              <span className="b4-card__mark">{f.value.slice(0, 1)}</span>
              <div className="b4-card__id">
                <div className="b4-card__name">{f.value}</div>
                <div className="b4-card__sub">{f.practices}</div>
              </div>
              <span className="b4-card__radio" />
            </div>
            <div className="b4-card__divider" />
            <div className="b4-card__focus-h">Interview focus</div>
            <div className="b4-card__chips">
              {f.focus.map((fa) => <span key={fa} className="focus-chip">{fa}</span>)}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function SetupScreen({ onStart, setupPayload, onBlocked }) {
  const [role, setRole] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [difficulty, setDifficulty] = useState("Beginner");
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileDisabled, setMobileDisabled] = useState(false);
  const [isJobMode, setIsJobMode] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [prepareStatus, setPrepareStatus] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [jobDescFile, setJobDescFile] = useState(null);
  const [interviewCategory, setInterviewCategory] = useState("generic");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [preSessionId, setPreSessionId] = useState(null);
  const [preGreetingStatus, setPreGreetingStatus] = useState("idle");

  useEffect(() => {
    if (setupPayload && setupPayload.mode === "JOB") {
      setIsJobMode(true);
      setRole(setupPayload.role || "");
      setYearsExperience(String(setupPayload.experience || ""));
      setDifficulty(setupPayload.difficulty || "Intermediate");
      setMobileDisabled(true);
      setTimeout(() => { handleAutoStart(setupPayload); }, 500);
    }
  }, [setupPayload]);

  useEffect(() => {
    const generatePreGreeting = async () => {
      try {
        const token = await getAuthToken();
        if (!token) { setPreGreetingStatus("failed"); return; }
        const formData = new FormData();
        formData.append("role", role || "Software Engineer");
        formData.append("mode", isJobMode ? "COMPANY" : "PRACTICE");
        formData.append("username", auth.currentUser?.displayName || "User");
        setPreGreetingStatus("generating");
        const response = await fetch(`${API_BASE}/interview/pre_greeting`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (response.ok) {
          const data = await response.json();
          setPreSessionId(data.pre_session_id);
          setPreGreetingStatus("generating");
        } else {
          setPreGreetingStatus("failed");
        }
      } catch {
        setPreGreetingStatus("failed");
      }
    };
    if (!isJobMode) generatePreGreeting();
  }, [isJobMode]);

  useEffect(() => { setSelectedCompany(""); }, [interviewCategory]);

  const categoryCopy = INTERVIEW_CATEGORIES.find((item) => item.value === interviewCategory);

  const adjustExp = (delta) => {
    const current = parseInt(yearsExperience) || 0;
    setYearsExperience(String(Math.max(0, Math.min(30, current + delta))));
  };

  const buildJdContextFile = () => {
    if (interviewCategory === "generic") return null;
    const companyLine = selectedCompany ? `Target company: ${selectedCompany}` : "Target company: Not selected";
    const focusLine = categoryCopy ? `Interview category: ${categoryCopy.label}. Focus: ${categoryCopy.description}` : "";
    const content = `${focusLine}\n${companyLine}`.trim();
    return content ? new File([content], "vagmee-interview-context.txt", { type: "text/plain" }) : null;
  };

  const appendSharedFormData = (formData, config) => {
    formData.append("username", auth.currentUser?.displayName || auth.currentUser?.email?.split("@")[0] || "Candidate");
    formData.append("role", config.role);
    formData.append("years_experience", config.years_experience);
    formData.append("difficulty", config.difficulty);
    formData.append("mobile_number", config.mobile_number || "");
    if (config.pre_session_id) formData.append("pre_session_id", config.pre_session_id);
    if (config.job_id) formData.append("job_id", config.job_id);
    if (config.b2b_interview_mode) formData.append("b2b_interview_mode", config.b2b_interview_mode);
    if (config.selected_company) formData.append("target_company", config.selected_company);
    if (config.interview_category) formData.append("interview_category", config.interview_category);
    if (config.uploaded_questions?.length) formData.append("uploaded_questions", JSON.stringify(config.uploaded_questions));
    if (resumeFile) formData.append("resume", resumeFile);
    const jdContextFile = buildJdContextFile();
    if (jobDescFile) formData.append("job_description", jobDescFile);
    else if (jdContextFile) formData.append("job_description", jdContextFile);
    else if (config.job_description) formData.append("job_description", config.job_description);
  };

  const startGreetingFlow = async (config, statusText) => {
    setPreparing(true);
    setPrepareStatus(statusText);
    try {
      const token = await getAuthToken();
      if (!token) {
        toast.error("Authentication failed. Please try logging in again.");
        setPreparing(false);
        return;
      }
      const formData = new FormData();
      appendSharedFormData(formData, config);
      const greetingRes = await fetch(`${API_BASE}/get_greeting`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!greetingRes.ok) {
        const errorData = await greetingRes.json().catch(() => ({}));
        setPreparing(false);
        // Freemium Conversion Funnel: when the caller opts in via onBlocked
        // (only wired up when the funnel is enabled), any failure to start
        // an interview here means the user's free/paid access ran out —
        // route to the paywall instead of a dead-end error toast.
        if (onBlocked) {
          onBlocked(errorData);
          return;
        }
        toast.error(`Failed to start interview: ${errorData.message || "Unknown error"}`);
        return;
      }
      const greetingData = await greetingRes.json();
      const sessionId = greetingData.session_id;
      const sessionTotalQuestions = greetingData.total_questions || 10;
      const sessionIsTrial = greetingData.is_trial || false;
      setPrepareStatus("Preparing your greeting...");
      const pollGreetingStatus = async () => {
        try {
          const statusRes = await fetch(`${API_BASE}/interview/session/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!statusRes.ok) throw new Error(`Status check failed: ${statusRes.status}`);
          const statusData = await statusRes.json();
          const greetingStatus = statusData.greeting?.status;
          if (greetingStatus === "READY") {
            const greetingTimeline =
              statusData.greeting?.timeline ||
              statusData.greeting?.phonemes ||
              statusData.greeting?.visemes ||
              statusData.timeline ||
              [];
            setPrepareStatus("Starting interview...");
            setTimeout(() => {
              setPreparing(false);
              const username = auth.currentUser?.displayName || auth.currentUser?.email?.split("@")[0] || "Candidate";
              onStart({
                ...config,
                session_id: sessionId,
                interview_category: interviewCategory,
                selected_company: selectedCompany,
                greeting: {
                  status: "READY",
                  text: `Hello ${username}, welcome to your ${config.role} mock interview${config.selected_company ? ` for ${config.selected_company}` : ""}. Let's begin.`,
                  audio_url: statusData.greeting.audio_url,
                  duration: statusData.greeting.duration,
                  timeline: greetingTimeline,
                },
                total_questions: sessionTotalQuestions,
                is_trial: sessionIsTrial,
              });
            }, 500);
          } else if (greetingStatus === "FAILED") {
            toast.error(`Failed to generate greeting: ${statusData.greeting?.error || "Greeting generation failed"}`);
            setPreparing(false);
          } else {
            setTimeout(pollGreetingStatus, 1500);
          }
        } catch {
          setTimeout(pollGreetingStatus, 2000);
        }
      };
      pollGreetingStatus();
    } catch {
      toast.error("Failed to start interview. Please try again.");
      setPreparing(false);
    }
  };

  const handleAutoStart = async (payload) => {
    if (!payload.role?.trim()) {
      toast.error("Interview configuration error: Role is missing");
      return;
    }
    const config = {
      mode: "JOB",
      role: payload.role,
      years_experience: parseInt(payload.experience) || 0,
      difficulty: payload.difficulty,
      job_id: payload.job_id,
      job_description: payload.job_description,
      b2b_interview_mode: payload.b2b_interview_mode,
      uploaded_questions: payload.uploaded_questions || [],
      mobile_number: mobileNumber || "",
    };
    await startGreetingFlow(config, "Starting your company interview...");
  };

  const handleStart = async () => {
    if (!mobileNumber.trim()) { toast.error("Mobile number is required"); return; }
    if (mobileNumber.length !== 10) { toast.error("Mobile number must be exactly 10 digits"); return; }
    if (!role.trim()) { toast.error("Interview Role is required"); return; }
    if (!yearsExperience.trim()) { toast.error("Years of Experience is required"); return; }
    const config = {
      role,
      years_experience: parseInt(yearsExperience) || 0,
      difficulty,
      mobile_number: mobileNumber,
      pre_session_id: preSessionId,
      interview_category: interviewCategory,
      selected_company: selectedCompany,
      b2b_interview_mode: interviewCategory !== "generic" ? interviewCategory.toUpperCase() : undefined,
    };
    await startGreetingFlow(config, "Starting your interview session...");
  };

  return (
    <div className="setup-screen">
      <div className="setup-product-shell">

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="setup-header setup-header--product">
          <span className="setup-eyebrow">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Interview Practice
          </span>
          <h1 className="setup-title">Interview Readiness Studio</h1>
          <p className="setup-subtitle">Practice realistic interview scenarios with AI-driven feedback to improve confidence and interview performance.</p>
        </div>

        {/* ── Track tabs ─────────────────────────────────────────── */}
        <div className="setup-track-tabs">
          {INTERVIEW_CATEGORIES.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`setup-track-tab${interviewCategory === item.value ? " is-on" : ""}`}
              onClick={() => setInterviewCategory(item.value)}
            >
              <span className="setup-track-tab__icon"><CategoryIcon type={item.value} /></span>
              <span className="setup-track-tab__copy">
                <strong>{item.label}</strong>
                <span>{item.sub}</span>
              </span>
            </button>
          ))}
        </div>

        {/* ── Two-column grid ────────────────────────────────────── */}
        <div className="setup-product-grid">

          {/* Left: track-specific content */}
          <main className="setup-main-flow">
            {interviewCategory === "generic" && <GeneralPanel />}
            {interviewCategory === "service" && (
              <ServicePanel selectedCompany={selectedCompany} onSelect={setSelectedCompany} />
            )}
            {interviewCategory === "big4" && (
              <Big4Panel selectedFirm={selectedCompany} onSelect={setSelectedCompany} />
            )}
          </main>

          {/* Right: all config fields + CTA */}
          <aside className="setup-rail">
            <div className="setup-rail__sticky">
              <section className="setup-config-card">
                <div className="setup-config-card__head">
                  <span>Session Config</span>
                  <strong>{categoryCopy?.label}</strong>
                </div>

                {/* Role */}
                <div className="form-group">
                  <label htmlFor="role">Role <span className="field-req">*</span></label>
                  <input
                    type="text"
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g., Software Engineer, PM, Data Analyst"
                    className="form-input"
                  />
                </div>

                {/* Experience */}
                <div className="form-group">
                  <label>Experience <span className="field-req">*</span></label>
                  <div className="exp-stepper">
                    <button type="button" className="exp-btn" onClick={() => adjustExp(-1)}>-</button>
                    <input
                      type="number"
                      value={yearsExperience}
                      onChange={(e) => setYearsExperience(e.target.value)}
                      min="0"
                      max="30"
                      placeholder="0"
                      className="form-input exp-input"
                    />
                    <span className="exp-unit">yrs</span>
                    <button type="button" className="exp-btn" onClick={() => adjustExp(1)}>+</button>
                  </div>
                  <div className="exp-chips">
                    {EXP_QUICK.map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={`exp-chip${yearsExperience === v ? " is-on" : ""}`}
                        onClick={() => setYearsExperience(v)}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty */}
                <div className="form-group">
                  <label>Difficulty <span className="field-req">*</span></label>
                  <div className="diff-pills">
                    {DIFFICULTY_OPTIONS.map(({ value, note }) => (
                      <button
                        key={value}
                        type="button"
                        className={`diff-pill${difficulty === value ? " is-on" : ""}`}
                        onClick={() => setDifficulty(value)}
                      >
                        <span className="diff-pill__label">{value}</span>
                        <span className="diff-pill__note">{note}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mobile */}
                <div className="form-group">
                  <label htmlFor="mobile">Mobile <span className="field-req">*</span></label>
                  {/* <span className="form-sublabel" style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem", display: "block", marginBottom: "0.35rem" }}>
                    WhatsApp feedback sent here
                  </span> */}
                  <div className="phone-wrap">
                    <span className="phone-cc">+91</span>
                    <input
                      type="tel"
                      id="mobile"
                      value={mobileNumber}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "");
                        if (v.length <= 10) setMobileNumber(v);
                      }}
                      placeholder="999 XX 999 XX"
                      className="form-input phone-field"
                      disabled={mobileDisabled}
                    />
                  </div>
                </div>

                {/* Files */}
                <div className="setup-files">
                  {/* Resume */}
                  <div className="form-group">
                    <label>Resume <span className="field-opt">optional</span></label>
                    <label htmlFor="resume" className="file-upload-btn">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 16V3m0 0l-5 5m5-5l5 5" /><path d="M20 21H4" />
                      </svg>
                      <span>{resumeFile ? resumeFile.name : "Upload Resume"}</span>
                      <input
                        type="file"
                        id="resume"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setResumeFile(e.target.files[0] || null)}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>

                  {/* Job Description — upload only */}
                  <div className="form-group">
                    <label>Job Description <span className="field-opt">optional</span></label>
                    <label htmlFor="jobdesc" className="file-upload-btn">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 16V3m0 0l-5 5m5-5l5 5" /><path d="M20 21H4" />
                      </svg>
                      <span>{jobDescFile ? jobDescFile.name : "Upload JD"}</span>
                      <input
                        type="file"
                        id="jobdesc"
                        accept=".pdf,.docx"
                        onChange={(e) => setJobDescFile(e.target.files[0] || null)}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>

                {/* CTA */}
                <button onClick={handleStart} className="start-button" disabled={preparing}>
                  {preparing ? (
                    <>
                      <span className="start-spinner" />
                      {prepareStatus}
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                      Start Interview
                    </>
                  )}
                </button>

                <p className="setup-legal">
                  By starting you agree to our{" "}
                  <span style={{ color: "rgba(255,255,255,0.6)" }}>Terms of Service</span>
                </p>
              </section>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
